(ns frontend.view.modals.curtains.fundraising-opportunity.lost-curtain
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.curtains.fundraising-opportunity.events :as events]
   [frontend.view.modals.curtains.opportunity-load :as opportunity-load]
   [reagent.core :as r]
   [ui.color :as c]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.state.named-values :as named-values]))

(defonce modal-key :curtain.fundraising-opportunity/lost)

(def lost-reason-attribute :fundraising-opportunity/lost-reason)

(defn entity->opt
  [e]
  (when e
    {:value (:db/id e)
     :label (entity/display-string e)}))

(defn create-loss-reason!
  [edit-state attribute input-str]
  (f-entity/open-creator!
   [:entity.type/named-value]
   {:attribute               attribute
    :named-value-attribute   attribute
    :mandatory-entity-fields [:named-value/value]
    :start-value             input-str
    :skip-success-msg?       true
    :on-ok!                  (fn [{:keys [new-entity-id new-entity close-cb!]}]
                               ;; The search index does not have the new reason yet.
                               (when new-entity
                                 (named-values/remember-row!
                                  (assoc new-entity
                                         :db/id new-entity-id
                                         :named-value/attribute attribute)))
                               (let [opt {:value new-entity-id
                                          :label (entity/display-string new-entity)}]
                                 (swap! edit-state assoc
                                        attribute new-entity-id
                                        :lost-reason-opt opt))
                               (when (fn? close-cb!) (close-cb!)))}))

(defn lost-reason-selector
  [edit-state pipeline-id]
  (let [value-opt (or (:lost-reason-opt @edit-state)
                      (->> (get @edit-state lost-reason-attribute)
                           (get @session/entities)
                           entity->opt))
        admin? (session/is-admin?)
        base {:editor-type               :editor.type/search
              :data-type                 :data.type/ref-single
              :label                     (loc/tr :fundraising-opportunity-lost-curtain/record-why-rejected)
              :mandatory?                true
              :placeholder               (loc/tr :loss-reason-placeholder)
              :field/entity-types        [:entity.type/named-value]
              :field/select-entity-types [:entity.type/named-value]
              :named-value-attribute     lost-reason-attribute
              ;; Without the pipeline, a reason that is archived in it is offered.
              :creation-pipeline-id      pipeline-id
              :is-clearable?             false
              :value                     value-opt
              :on-change                 (fn [_value option]
                                           (swap! edit-state assoc
                                                  lost-reason-attribute (:value option)
                                                  :lost-reason-opt option))}
        args (if admin?
               (assoc base
                      :field/create-entity-types [:entity.type/named-value]
                      :create! (fn [input-str]
                                 (create-loss-reason! edit-state lost-reason-attribute input-str)))
               base)]
    [labelled-input/view args]))

(defn internal-comment-input
  [edit-state]
  [labelled-input/view
   {:data-type :data.type/text-simple
    :label     (loc/tr :fundraising-opportunity-lost-curtain/internal-loss-comment)
    :value     (:internal-comment @edit-state)
    :on-change (fn [new-value]
                 (if (nil? new-value)
                   (swap! edit-state dissoc :internal-comment)
                   (swap! edit-state assoc :internal-comment new-value)))}])

(defn loaded-view
  "The curtain, for an opportunity that is loaded. The opportunity is
   `:opportunity` in the arguments."
  [{:keys [_shown? _topmost? _close-modal! _props state _class-name _opportunity]}]
  (let [edit-state (r/cursor state [:edit])]
    (fn [{:keys [shown? topmost? close-modal! props state _class-name opportunity]}]
      (let [{:keys [reset-focus!]} props
            session-entities @session/entities
            fundraising-opportunity opportunity
            pipeline-id (pipeline/containing-pipeline-id fundraising-opportunity)
            fundraising-opportunity-name (:fundraising-opportunity/opportunity-name fundraising-opportunity)
            header-text (loc/tr :fundraising-opportunity-loss-curtain/title [fundraising-opportunity-name])
            loss-reasons (->> (named-values/usable-rows lost-reason-attribute pipeline-id)
                              (sort-by :common/ord))
            close-modal! (fn []
                           (close-modal!)
                           (when reset-focus!
                             (reset-focus!)))
            ;; Seed the first reason as the default here rather than once when the
            ;; curtain mounts: these options are named-values, which on the new
            ;; grid arrive from the search index a moment later, so a mount-time
            ;; default read nothing and left the form unsubmittable. Guarded on
            ;; the value being unset, so it runs once and cannot re-render loop.
            ;; The option is seeded too, because the search selector labels its
            ;; value from it and an index row is not in @session/entities.
            _ (when (and (seq loss-reasons)
                         (nil? (get @edit-state lost-reason-attribute)))
                (let [first-loss-reason (first loss-reasons)]
                  (swap! edit-state assoc
                         lost-reason-attribute (:db/id first-loss-reason)
                         :lost-reason-opt (entity->opt first-loss-reason))))
            edit-vals @edit-state
            ;; A chosen id is usable. Do not look up the reason to decide this.
            ;; A named-value from the index is not in @session/entities. A reason
            ;; that the user creates inline is in neither the session (custom
            ;; creator) nor the index cache. The Confirm button then stayed
            ;; disabled until a reload. Only a reason that the session knows
            ;; as disabled blocks the button.
            loss-reason-id (get edit-vals lost-reason-attribute)
            button-disabled? (or (nil? loss-reason-id)
                                 (:common/disabled? (get session-entities loss-reason-id)))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :color         ::c/danger
          :close-button? true}
         [:<>
          [:i {:class "fal fa-ban mr-2"}]
          header-text]
         [:div
          {:class "flex flex-col gap-4"}
          [lost-reason-selector edit-state pipeline-id]
          [internal-comment-input edit-state]]
         [:<>
          [modal/left-cancel-only close-modal! nil]
          [modal/right-single-button
           ::c/danger
           button-disabled?
           (fn [_]
             (events/confirm-loss state fundraising-opportunity close-modal!))
           (loc/tr :fundraising-opportunity-loss-curtain/save)
           nil]]]))))

(defn view
  [{:keys [props] :as args}]
  [opportunity-load/with-opportunity
   {:opportunity-id (:fundraising-opportunity-id props)
    :args           args
    :loaded-view    loaded-view
    :header-text    [:<>
                     [:i {:class "fal fa-ban mr-2"}]
                     (loc/tr :fundraising-opportunity-loss-curtain/title [nil])]}])
